import { Upload } from 'antd';
import ImgCrop from 'antd-img-crop';
import axios, { AxiosProgressEvent } from 'axios';
import React, { useRef } from 'react';

import { RcFile, UploadFile, UploadProps } from 'antd/lib/upload/interface';
import { BlobIndexTag, BlobMetadataField } from '../../../generated';
import { FileValidator } from './file-validator';
import sanitize from "sanitize-filename";


export interface AzureBlobResponse {
  versionId: string;
  blobUrl: string;
}

export interface AuthResult {
  isAuthorized: boolean;
  authHeader: {
    authHeader?: string;
    requestDate?: string;
    indexTags?: BlobIndexTag[];
    metadataFields?: BlobMetadataField[];
    blobPath?: string;
    blobName?: string;
  };
}
interface AzureUploadProps {
  data: {
    permittedContentTypes?: string[];
    permittedExtensions?: string[];
    maxFileSizeBytes?: number;
    maxWidthOrHeight?: number;
    notificationCount?: number;
  };
  onInvalidContentType: () => void;
  onInvalidContentLength: () => void;
  onSuccess: (response: AzureBlobResponse, file: UploadFile) => Promise<void>;
  onError?: (file: File, error: any) => void;
  onRemoveRequested?: (file: UploadFile<unknown>) => Promise<boolean>;
  authorizeRequest: (file: File) => Promise<AuthResult>;
  cropperProps?: object;
  uploadProps?: UploadProps;
  children?: React.ReactNode;
  maxFileCount?: number;
  buttonText?: string;
  uploadPreviewImage?: boolean;
}

export const AzureUpload: React.FC<AzureUploadProps> = ({ uploadPreviewImage: uploadImage = true, ...props }) => {
  const authResultRef = useRef<AuthResult | undefined>(undefined);
  function renameFile(originalFile: RcFile, newName: string) {
    try {
      return new File([originalFile], newName, {
        type: originalFile.type,
        lastModified: originalFile.lastModified
      });
    } catch (error) {
      console.error('Error occurred while renaming file:', error);
      return originalFile;
    }
  }
  const beforeUpload = async (file: RcFile) => {
    let sanitizedFilename = sanitize(file.name)
    sanitizedFilename = sanitizedFilename.replace(/[\s–—]+/g, '-');     
    let sanitizedFile: RcFile = renameFile(file, sanitizedFilename) as RcFile;
    sanitizedFile.uid = file.uid;

    const validatorOptions = {
      maxFileSizeBytes: props.data.maxFileSizeBytes,
      maxWidthOrHeight: props.data.maxWidthOrHeight,
      permittedContentTypes: props.data.permittedContentTypes
    };

    const validator = new FileValidator(sanitizedFile, validatorOptions);
    const result = await validator.validate();

    if (!result.success) {
      console.error(`Error: ${result.message}`);

      switch (result.code) {
        case 'content-type':
          props.onInvalidContentType();
          break;
        case 'content-length':
          props.onInvalidContentLength();
          break;
        case 'default':
          console.log(`File did not upload, error: ${JSON.stringify(result.message)}`);
          props.onError && props.onError(sanitizedFile, result.message);
          break;
      }

      return Upload.LIST_IGNORE;
    }

    const newFile = result.file;

    if (newFile) {
      const result = await props.authorizeRequest(newFile);
      if (result.isAuthorized) {
        //@ts-ignore
        newFile['url'] = result.blobPath;
        console.log('file', newFile);
        authResultRef.current = result;
        return newFile;
      } else {
        props.onError && props.onError(newFile, 'Error authorizing request');
      }
    }
    return Upload.LIST_IGNORE;
  };

  const getMetadataHeaders = (metadataFields?: BlobMetadataField[]) => {
    let metadataHeaders: any = {};
    if (metadataFields) {
      for (const [_, value] of Object.entries(metadataFields)) {
        metadataHeaders['x-ms-meta-' + value.name] = value.value;
      }
    }
    return metadataHeaders;
  };

  const getIndexTagsHeaders = (indexTags?: BlobIndexTag[]) => {
    let indexTagsHeaders: any = {};
    if (indexTags) {
      const output = indexTags.reduce((acc: Record<string, string>, cur: { name: string; value: string }) => {
        acc[cur.name] = cur.value;
        return acc;
      }, {});

      indexTagsHeaders['x-ms-tags'] = new URLSearchParams(output).toString();
    }
    return indexTagsHeaders;
  };

  const customizeUpload = async (option: any) => {
    if (authResultRef?.current?.isAuthorized) {
      const { authHeader } = authResultRef.current;

      try {
        option.file.url = authHeader.blobPath;

        let metadataHeaders: any = getMetadataHeaders(authHeader.metadataFields);

        let indexTagsHeaders: any = getIndexTagsHeaders(authHeader.indexTags);

        const isValidProgressEvent = (progressEvent: AxiosProgressEvent) => {
          return progressEvent?.total !== undefined && progressEvent.loaded !== undefined;
        };

        const headers = {
          ...option.headers,
          ...metadataHeaders,
          ...indexTagsHeaders,
          Authorization: authHeader.authHeader,
          'x-ms-blob-type': 'BlockBlob',
          'x-ms-version': '2021-04-10',
          'x-ms-date': authHeader.requestDate,
          'Content-Type': option.file.type
        };
        console.log('headers', JSON.stringify(headers));
        let response = await axios.request({
          method: 'put',
          url: authHeader.blobPath,
          data: new Blob([option.file], { type: option.file.type }),
          headers: headers,
          onUploadProgress: (progressEvent) => {
            if (isValidProgressEvent(progressEvent) && progressEvent.total !== undefined) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              option.onProgress({ percent: percentCompleted }, option.file);
            }
          }
        });
        const blobResponse: AzureBlobResponse = {
          versionId: response.headers['x-ms-version-id'],
          blobUrl: response.request.responseURL
        };
          props.onSuccess(blobResponse, option.file);
      } catch (uploadError) {
        if (props.onError) {
          console.log('test');
          props.onError(option.file, uploadError);
        }
        option.onError(uploadError, option.file);
      }
    } else {
      // not authorized - do nothing
    }
    return option;
  };

  const validFileExtensions = props.data.permittedExtensions ? '.' + props.data.permittedExtensions.join(', .') : undefined;

  if (props.cropperProps) {
    return (
      <ImgCrop {...props.cropperProps}>
        <Upload
          accept={validFileExtensions}
          customRequest={customizeUpload}
          beforeUpload={beforeUpload}
          onRemove={(file) => {
            return props.onRemoveRequested ? props.onRemoveRequested(file) : undefined;
          }}
          showUploadList={false}
          progress={{
            strokeColor: {
              '0%': '#108ee9',
              '100%': '#87d068'
            },
            strokeWidth: 3,
            format: (percent) => (percent ? `${parseFloat(percent.toFixed(2))}%` : '')
          }}
          {...props.uploadProps}
        >
          {props.children}
        </Upload>
      </ImgCrop>
    );
  }
  return (
    <Upload
      accept={validFileExtensions}
      customRequest={customizeUpload}
      beforeUpload={beforeUpload}
      onRemove={(file) => {
        return props.onRemoveRequested ? props.onRemoveRequested(file) : undefined;
      }}
      maxCount={props.maxFileCount}
      showUploadList={false}
      progress={{
        strokeColor: {
          '0%': '#108ee9',
          '100%': '#87d068'
        },
        strokeWidth: 3,
        format: (percent) => (percent ? `${parseFloat(percent.toFixed(2))}%` : '')
      }}
      {...props.uploadProps}
    >
      {props.children}
    </Upload>
  );
};
